import { v4 as uuid } from '@lukeed/uuid';
import { expandN } from 'regex-to-strings';
import { match, P } from 'ts-pattern';
import type { ParsedSchema, ParsedSchemaWithRef } from '@interxfi/j5-parse/types/parsed';
import { memoize } from '@interxfi/utilities/cache/memoize';

// Property name is just used to generate consistent examples, since the function is memoized based on arguments.
const generateUuid = memoize(function generateUuid(_propertyName: string) {
  return uuid();
});

// As per https://github.com/interxfi/j5 in the section about data type encoding
export function getScalarTypeForSchema(schema: ParsedSchema) {
  return match(schema)
    .with({ string: P.not(P.nullish) }, () => 'string' as const)
    .with({ bool: P.not(P.nullish) }, () => 'boolean' as const)
    .with({ float: P.not(P.nullish) }, () => 'number' as const)
    .with({ decimal: P.not(P.nullish) }, () => 'string' as const)
    .with({ bytes: P.not(P.nullish) }, () => 'string' as const)
    .with({ date: P.not(P.nullish) }, () => 'string' as const)
    .with({ timestamp: P.not(P.nullish) }, () => 'string' as const)
    .with({ key: P.not(P.nullish) }, () => 'string' as const)
    .with({ integer: { format: P.string.endsWith('64') } }, () => 'string' as const)
    .with({ integer: P.not(P.nullish) }, () => 'number' as const)
    .otherwise(() => undefined);
}

export function pickSchemaExample(schema: ParsedSchema) {
  return match(schema)
    .with({ enum: P.not(P.nullish) }, (s) => s.enum.example)
    .with({ integer: P.not(P.nullish) }, (s) => s.integer.example)
    .with({ float: P.not(P.nullish) }, (s) => s.float.example)
    .with({ map: P.not(P.nullish) }, (s) => s.map.example)
    .with({ any: P.not(P.nullish) }, (s) => s.any.example)
    .with({ bool: P.not(P.nullish) }, (s) => s.bool.example)
    .with({ array: P.not(P.nullish) }, (s) => s.array.example)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.example)
    .with({ object: P.not(P.nullish) }, (s) => s.object.example)
    .with({ key: P.not(P.nullish) }, (s) => s.key.example)
    .with({ string: P.not(P.nullish) }, (s) => s.string.example)
    .with({ decimal: P.not(P.nullish) }, (s) => s.decimal.example)
    .with({ date: P.not(P.nullish) }, (s) => s.date.example)
    .with({ timestamp: P.not(P.nullish) }, (s) => s.timestamp.example)
    .otherwise(() => undefined);
}

const expandPattern = memoize(function expandPattern(pattern: string, _propertyName?: string) {
  return expandN(new RegExp(pattern), 1)[0];
});

const stringToNumericHash = memoize(function stringToNumericHash(str: string, minRange: number = 0, maxRange: number = 9999) {
  let hash = 0;

  for (let i = 0; i < str.length; i += 1) {
    const charCode = str.charCodeAt(i);

    hash = (hash << 5) - hash + charCode;
  }

  // Ensure the hash is within the specified range
  const range = maxRange - minRange + 1;

  return Math.round(minRange + (Math.abs(hash) % range));
});

const generateExampleNumber = memoize(function generateExampleNumber(
  minimum: number | string | undefined,
  maximum: number | string | undefined,
  stringOrNumeric: 'string' | 'number',
  format: string | undefined,
) {
  const min = minimum !== undefined && minimum !== '' ? Number(minimum) : 0;
  const max = maximum !== undefined && maximum !== '' ? Number(maximum) : 100_000;
  const wholePart = stringToNumericHash('ixf', min, max);
  const fractionalPart = stringToNumericHash(`${'ixf'}-decimal`, min, max);

  const asNumber = Number(`${wholePart}.${fractionalPart}`);

  const fractionalDigits = match(format?.toLowerCase())
    .with(P.union('float', 'float32', 'float64', 'decimal', 'double'), () => 2)
    .otherwise(() => 0);

  const fixed = asNumber.toFixed(fractionalDigits);

  return stringOrNumeric === 'number' ? Number(fixed) : fixed;
});

export function exampleSchema(
  schema: ParsedSchemaWithRef,
  schemas: Map<string, ParsedSchema>,
  examples: Map<string, any>,
  inProgressCache: Set<string> = new Set(),
  propertyName?: string,
) {
  return match(schema)
    .with({ $ref: P.not(P.nullish) }, (s) => {
      if (inProgressCache.has(s.$ref)) {
        return undefined;
      }

      if (examples.has(s.$ref)) {
        return examples.get(s.$ref);
      }

      inProgressCache.add(s.$ref);

      const ref = schemas.get(s.$ref.replace('#/schemas/', ''));

      if (ref) {
        examples.set(s.$ref, exampleSchema(ref, schemas, examples, inProgressCache, propertyName));
      }

      inProgressCache.delete(s.$ref);

      return examples.get(s.$ref);
    })
    .with({ enum: P.not(P.nullish) }, (s) => {
      if (s.enum.example) {
        return s.enum.example;
      }

      s.enum.example = match(s.enum.name)
        .with('Currency', () => 'USD')
        .with('Country', () => 'US')
        .otherwise(() => s.enum.options?.[1]?.name || s.enum.options?.[0]?.name || 'UNSPECIFIED');

      return s.enum.example;
    })
    .with({ integer: P.not(P.nullish) }, (s) => {
      if (s.integer.format) {
        s.integer.format = s.integer.format.toLowerCase();
      }

      s.integer.example =
        s.integer.example ??
        generateExampleNumber(
          s.integer.rules.minimum || 0,
          s.integer.rules.maximum ?? 9999,
          (getScalarTypeForSchema(s) || 'string') as 'string' | 'number',
          s.integer.format,
        );

      return s.integer.example;
    })
    .with({ float: P.not(P.nullish) }, (s) => {
      if (s.float.format) {
        s.float.format = s.float.format.toLowerCase();
      }

      s.float.example =
        s.float.example ??
        generateExampleNumber(
          s.float.rules.minimum || 0,
          s.float.rules.maximum ?? 9999,
          (getScalarTypeForSchema(s) || 'number') as 'string' | 'number',
          s.float.format,
        );

      return s.float.example;
    })
    .with({ map: P.not(P.nullish) }, (s) => {
      s.map.example = s.map.example ?? {
        [exampleSchema(s.map.keySchema, schemas, examples, inProgressCache, [propertyName, 'key'].filter(Boolean).join('_')) || 'key']:
          exampleSchema(s.map.itemSchema, schemas, examples, inProgressCache, [propertyName, 'value'].filter(Boolean).join('_')) || 'value',
      };

      return s.map.example;
    })
    .with({ any: P.not(P.nullish) }, (s) => {
      s.any.example = s.any.example ?? {};

      return s.any.example;
    })
    .with({ bool: P.not(P.nullish) }, (s) => {
      s.bool.example = s.bool.example ?? true;

      return s.bool.example;
    })
    .with({ array: P.not(P.nullish) }, (s) => {
      if (s.array.example !== undefined) {
        return s.array.example;
      }

      const generated = exampleSchema(s.array.itemSchema, schemas, examples, inProgressCache, propertyName);

      if (generated) {
        s.array.example = [generated];
      }

      return s.array.example;
    })
    .with({ oneOf: P.not(P.nullish) }, (s) => {
      if (s.oneOf.example !== undefined) {
        return s.oneOf.example;
      }

      const example: Record<string, any> = {};

      for (const [key, value] of s.oneOf.properties) {
        const accessor = `${propertyName ? `${propertyName}.` : ''}${key}`;
        example[key] = exampleSchema(value.schema, schemas, examples, inProgressCache, accessor);
      }

      s.oneOf.example = example;

      examples.set(s.oneOf.fullGrpcName, example);

      return example;
    })
    .with({ polymorph: P.not(P.nullish) }, (s) => {
      if (s.polymorph.example !== undefined) {
        return s.polymorph.example;
      }

      const example: Record<string, any> = {};

      for (const [key, value] of s.polymorph.properties || []) {
        const valueProp = value.get('value');

        if (valueProp) {
          const accessor = `${propertyName ? `${propertyName}.` : ''}${key}`;
          example[key] = exampleSchema(valueProp.schema, schemas, examples, inProgressCache, accessor);
        }
      }

      s.polymorph.example = example;
      examples.set(s.polymorph.fullGrpcName, example);

      return example;
    })
    .with({ object: P.not(P.nullish) }, (s) => {
      if (s.object.example !== undefined) {
        return s.object.example;
      }

      const example: Record<string, any> = {};

      for (const [key, value] of s.object.properties) {
        const accessor = `${propertyName ? `${propertyName}.` : ''}${key}`;
        example[key] = exampleSchema(value.schema, schemas, examples, inProgressCache, accessor);
      }

      s.object.example = example;
      examples.set(s.object.fullGrpcName, example);

      return example;
    })
    .with({ key: P.not(P.nullish) }, (s) => {
      s.key.example =
        s.key.example ??
        match(s.key.format)
          .with({ id62: P.not(P.nullish) }, () => expandPattern('^[0-9A-Za-z]{22}$', propertyName))
          .with({ custom: P.not(P.nullish) }, (c) =>
            c.custom.pattern ? expandPattern(c.custom.pattern) : generateUuid(propertyName || 'ixc-custom'),
          )
          .otherwise(() => generateUuid(propertyName || 'ixc-unknown'));

      return s.key.example;
    })
    .with({ string: P.not(P.nullish) }, (s) => {
      if (s.string.format) {
        s.string.format = s.string.format.toLowerCase();
      }

      const hashKey = `${propertyName || ''}`;

      s.string.example = match(s.string)
        .with({ example: P.not(P.nullish) }, () => s.string.example)
        .with({ rules: { pattern: P.not(P.nullish) } }, (patternedSchema) => {
          const lowercaseKey = propertyName?.toLowerCase() || '';

          if (lowercaseKey.includes('email')) {
            return 'test@example.com';
          }

          return expandPattern(patternedSchema.rules.pattern);
        })
        .with({ format: P.not(P.nullish) }, (formattedSchema) => {
          switch (formattedSchema.format.toLowerCase()) {
            case 'bytes':
              return 'bytes';
            case 'uuid':
              // This should be random if there's not a property name
              return propertyName ? generateUuid(propertyName) : uuid();
            case 'date-time':
              return '2023-08-15T00:00:00.000Z';
            case 'date':
              return '2006-01-02';
            case 'int64':
            case 'int32':
            case 'uint32':
            case 'uint64':
            case 'number':
              return `${stringToNumericHash(hashKey)}`;
            case 'double':
            case 'float':
            case 'float32':
            case 'float64':
            case 'decimal':
              return `${stringToNumericHash(hashKey)}.${stringToNumericHash(`${hashKey}-decimal`)}`;
            default:
              return 'string';
          }
        })
        .otherwise(() => 'string');

      return s.string.example;
    })
    .with({ decimal: P.not(P.nullish) }, (s) => {
      s.decimal.example =
        s.decimal.example ??
        generateExampleNumber(
          s.decimal.rules.minimum || 0,
          s.decimal.rules.maximum || 9999,
          (getScalarTypeForSchema(s) || 'string') as 'string' | 'number',
          'decimal',
        );

      return s.decimal.example;
    })
    .with({ date: P.not(P.nullish) }, (s) => {
      s.date.example = s.date.example ?? '2006-01-02';

      return s.date.example;
    })
    .with({ timestamp: P.not(P.nullish) }, (s) => {
      s.timestamp.example = s.timestamp.example ?? '2023-08-15T00:00:00.000Z';

      return s.timestamp.example;
    })
    .otherwise(() => undefined);
}
